import clsx from 'clsx';
import { noop } from 'lodash-es';
import { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CalendarIcon } from '@dynatrace/strato-icons';

import * as styles from './DateRangeSelector.sty.js';
import type { DateRangeSelectorProps, DateRangeSelectorRef } from './types.js';
import { Button } from '../../buttons/button/Button.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { useDateTimeRangePickerStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import { isVisualRefreshEnabled } from '../../core/utils/is-visual-refresh-enabled.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';
import { globalSprinkles } from '../../styles/safe-sprinkles.js';
import { CalendarRoot } from '../calendar/CalendarRoot.js';
import { FormFieldMessageContext } from '../forms-core/contexts/FormFieldMessageContext.js';

/**
 * Calendar widget trigger and overlay component.
 */
export const DateRangeSelector = forwardRef<
  DateRangeSelectorRef,
  DateRangeSelectorProps
>((props, forwardedRef) => {
  const {
    disabled,
    readOnly,
    error,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    ...remainingProps
  } = props;
  const [open, setOpen] = useState(false);

  const intl = useIntl();
  const calendarTriggerLabel = intl.formatMessage({
    defaultMessage: 'DateRangePicker calendar trigger',
    id: 'K2M82PM/gcTo3fjq',
    description: 'Text shown for the datetimerangepicker calendar trigger.',
  });

  const clearCalendarValue = useDateTimeRangePickerStore(
    (state) => state.clearCalendarValue,
  );

  const {
    targetRef,
    triggerRef,
    overlayRef,
    overlayState,
    overlayProps,
    overlayTriggerProps,
  } = useOverlayWithTrigger({
    placement: 'bottom-end',
    offset: 8,
    shouldCloseOnBlur: false,
    open,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        clearCalendarValue?.();
      }
      setOpen(isOpen);
    },
    // the overlay should not open when disabled or readOnly
    disabled: disabled || readOnly,
  });

  const rootRef = useMergeRefs<HTMLButtonElement | null>([
    triggerRef,
    targetRef,
  ]);

  useImperativeHandle(
    forwardedRef,
    (): DateRangeSelectorRef => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
    }),
    [],
  );

  const { focusClassName } = useFocusRing({
    focusWithin: false,
    isMinimal: false,
    disabled: disabled,
    color: error ? 'critical' : 'neutral',
  });
  const formFieldMessageContextValue = useMemo(
    () => ({
      dispatch: noop,
      state: {
        customMessages: [],
        formControlMessages: [],
        isDisabled: disabled,
        showError: error,
      },
    }),
    [error, disabled],
  );

  // with the visual refresh enabled, only pure error state should receive critical coloring
  const isCritical = isVisualRefreshEnabled()
    ? error && !disabled && !readOnly
    : error;

  return (
    <>
      <Button
        ref={rootRef}
        data-testid={dataTestId}
        className={clsx(styles.selector, focusClassName, consumerClassName)}
        style={consumerStyle}
        aria-label={calendarTriggerLabel}
        {...remainingProps}
        {...overlayTriggerProps}
        size="condensed"
        color={isCritical ? 'critical' : 'neutral'}
        disabled={disabled || readOnly}
        aria-disabled={ariaDisabledProp}
        variant="emphasized"
        data-dt-name="ShowCalendar"
      >
        <Button.Prefix>
          <CalendarIcon />
        </Button.Prefix>
      </Button>
      <Overlay
        ref={overlayRef}
        isOpen={overlayState.isOpen}
        overlayProps={overlayProps}
        className={globalSprinkles({
          paddingLeft: 16,
          paddingTop: 16,
          paddingRight: 16,
          paddingBottom: 16,
        })}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        <FormFieldMessageContext.Provider value={formFieldMessageContextValue}>
          <CalendarRoot type="range" aria-disabled={ariaDisabledProp} />
        </FormFieldMessageContext.Provider>
      </Overlay>
    </>
  );
});
